export function extensionDownloadPath(version: string, local: boolean): string {
  compareGuideVersions(version, version);
  return `/downloads/cross-border-helper-extension-v${version}${local ? "-local" : ""}.zip`;
}

export type GuideChannel = "production" | "local";
export type GuideRequest = { origin: string; channel: GuideChannel; currentVersion?: string; scenario: "install" | "update" };
export type GuideRelease = { protocol: 1; channel: GuideChannel; latestVersion: string; minimumSupportedVersion: string; releasedAt: string; notes: string[]; installPath: "/extension" };
function versionParts(value: string): number[] {
  if (typeof value !== "string" || !/^(0|[1-9]\d*)(\.(0|[1-9]\d*)){0,3}$/.test(value)) throw new Error("INVALID_VERSION");
  const parts = value.split(".").map(Number);
  if (parts.some(part => part > 65535) || parts.every(part => part === 0)) throw new Error("INVALID_VERSION");
  return parts;
}
export function compareGuideVersions(a: string, b: string): number {
  const left = versionParts(a), right = versionParts(b);
  for (let index = 0; index < 4; index++) { const difference = (left[index] || 0) - (right[index] || 0); if (difference) return difference; }
  return 0;
}
/** Local/production selection comes from the actual trusted page, never the Web build's DEV flag. */
export function guideRequest(pageUrl: string, officialWebUrl = import.meta.env.VITE_WEB_BASE_URL || "https://alirecommend.com"): GuideRequest {
  const url = new URL(pageUrl);
  const local = ["localhost", "127.0.0.1"].includes(url.hostname);
  const official = new URL(officialWebUrl);
  if (url.username || url.password || (local ? !["http:", "https:"].includes(url.protocol) : url.protocol !== "https:" || url.origin !== official.origin)
    || url.searchParams.getAll("channel").length > 1 || url.searchParams.getAll("version").length > 1) throw new Error("INVALID_GUIDE_ORIGIN");
  const channel = local ? "local" : "production";
  const requestedChannel = url.searchParams.get("channel");
  const currentVersion = url.searchParams.get("version") ?? undefined;
  if ((url.searchParams.has("channel") && requestedChannel !== channel) || (currentVersion !== undefined && !requestedChannel)) throw new Error("INVALID_GUIDE_CHANNEL");
  if (currentVersion !== undefined) versionParts(currentVersion);
  return { origin: url.origin, channel, currentVersion, scenario: currentVersion ? "update" : "install" };
}
export function validateGuideRelease(value: unknown, request: GuideRequest): GuideRelease {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("INVALID_RELEASE");
  const record = value as Record<string, unknown>;
  if (Object.keys(record).some(key => !["protocol", "channel", "latestVersion", "minimumSupportedVersion", "releasedAt", "notes", "installPath"].includes(key))
    || record.protocol !== 1 || record.channel !== request.channel || record.installPath !== "/extension"
    || typeof record.latestVersion !== "string" || typeof record.minimumSupportedVersion !== "string" || compareGuideVersions(record.minimumSupportedVersion, record.latestVersion) > 0
    || typeof record.releasedAt !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(record.releasedAt) || !Number.isFinite(Date.parse(record.releasedAt))
    || !Array.isArray(record.notes) || record.notes.length > 8 || record.notes.some(note => typeof note !== "string" || !note.trim() || note.length > 500)) throw new Error("INVALID_RELEASE");
  return record as GuideRelease;
}
export async function fetchGuideRelease(request: GuideRequest, fetcher: typeof fetch = fetch): Promise<GuideRelease> {
  const metadataUrl = `${request.origin}/downloads/extension-release${request.channel === "local" ? "-local" : ""}.json`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8_000);
  const options: RequestInit = { credentials: "omit", redirect: "error", cache: "no-store", referrerPolicy: "no-referrer", signal: controller.signal };
  const trustedResponse = (response: Response, url: string) => response.ok && !response.redirected && (!response.url || response.url === url);
  try {
    const response = await fetcher(metadataUrl, { ...options, headers: { Accept: "application/json" } });
    if (!trustedResponse(response, metadataUrl) || !/^application\/json(?:\s*;|$)/i.test(response.headers.get("content-type") || "")) throw new Error("RELEASE_UNAVAILABLE");
    const body = await response.text();
    if (body.length > 12_000) throw new Error("INVALID_RELEASE_SIZE");
    const release = validateGuideRelease(JSON.parse(body), request);
    const zipUrl = `${request.origin}${extensionDownloadPath(release.latestVersion, request.channel === "local")}`;
    const zip = await fetcher(zipUrl, { ...options, method: "HEAD" });
    if (!trustedResponse(zip, zipUrl) || !/^application\/(?:zip|x-zip-compressed|octet-stream)(?:\s*;|$)/i.test(zip.headers.get("content-type") || "") || zip.headers.get("content-length") === "0") throw new Error("RELEASE_PACKAGE_UNAVAILABLE");
    return release;
  } finally { clearTimeout(timer); }
}

export function isMobileDevice(ua: string, platform: string, maxTouchPoints: number): boolean {
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua)
    || (platform === "MacIntel" && maxTouchPoints > 1);
}

export async function copyGuideText(
  text: string,
  clipboard: Pick<Clipboard, "writeText"> | undefined,
): Promise<"copied" | "manual"> {
  if (!clipboard) return "manual";
  try {
    await clipboard.writeText(text);
    return "copied";
  } catch {
    return "manual";
  }
}

export function focusChangedInstallStep(
  previousStep: number,
  step: number,
  heading: Pick<HTMLElement, "focus" | "scrollIntoView"> | null,
): void {
  if (previousStep === step || !heading) return;
  heading.focus({ preventScroll: true });
  heading.scrollIntoView({ behavior: "instant", block: "start" });
}

export const extensionTestQuestion = "请帮我看懂当前页面，告诉我下一步做什么。";

export const extensionBrowsers = {
  chrome: {
    name: "Chrome",
    address: "chrome://extensions",
    help: "https://developer.chrome.com/docs/extensions/get-started/tutorial/hello-world#load-unpacked",
  },
  edge: {
    name: "Edge",
    address: "edge://extensions",
    help: "https://learn.microsoft.com/en-us/microsoft-edge/extensions/getting-started/extension-sideloading",
  },
} as const;

export type ExtensionBrowser = keyof typeof extensionBrowsers;
